// src/pages/MedicalAssistant/MedicalAssistant.jsx
"use client";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./MedicalAssistant.css";
import AppLayout from "../../../navigation/Layout/Layout.jsx";
import { useTranslation } from "react-i18next";

import ReactWebChat, { createDirectLine } from "botframework-webchat";
import { useDispatch, useSelector } from "react-redux";
import { hbEnsureToken, hbGetToken, hbRefreshToken } from "../../redux/thunks/healthBotThunks.js";
import { selectHBToken, selectHBExpiresAt, selectHBDlEndpoint } from "../../redux/selectors/healthBotSelectors.js";
import { useTheme } from "../../../places/context/ThemeContext.jsx";
import RobotIllustration from "../../../assets/images/robot1.svg"; // ✅ robot centre
import RobotLeft from "../../../assets/images/gauche.svg";
import RobotRight from "../../../assets/images/droite.svg";
import { selectEffectiveUserId } from "../../../sous-profile/redux/selectors/sousProfileSelectors.js";
import Cookies from "js-cookie";
/** Scenario IDs must match those in Azure Health Bot exactly */
const scenarioMap = {
    guide: "feedback_collection_v1",
    q: "Answers_Based_On_Credible_Sources_copy",
    symptoms: "symptom_checker_v1",
    appointment: "appointment_booking_v1",
    drugs: "drugs_v1",
    emergency: "emergency_protocol_v1",
};

export default function MedicalAssistant({ onOpenHistory = () => {}, userName = "User" }) {
    const dispatch = useDispatch();
    const token = useSelector(selectHBToken);
    const expiresAt = useSelector(selectHBExpiresAt);
    const dlEndpoint = useSelector(selectHBDlEndpoint);
    const { isDark } = useTheme();
    const { t } = useTranslation();

    const [message, setMessage] = useState("");
    const [showChat, setShowChat] = useState(false);
    const [directLine, setDirectLine] = useState(null);
    const [isReady, setIsReady] = useState(false);

    const statusSubRef = useRef(null);
    const activitySubRef = useRef(null);
    const dlTokenRef = useRef(null);
    const firstTokenRef = useRef(true);
    const initSentRef = useRef(false);          // tracks InitConversation invoke sent
    const conversationReadyRef = useRef(false); // treat ready after InitConversation is posted
    const restartingRef = useRef(false);        // prevent rapid double restarts

    // Stable user id
    const effectiveUserId = useSelector(selectEffectiveUserId);
    const userId = useMemo(() => {
        const local = effectiveUserId || Cookies.get("userId") || "guest";
        return local.startsWith("dl_") ? local : `dl_${local}`;
    }, [effectiveUserId]);

    // Direct Line domain (you kept the global endpoint)
    const directLineDomain = useMemo(() => {
        const base = (dlEndpoint || "https://directline.botframework.com").replace(/\/+$/, "");
        return `${base}/v3/directline`;
    }, [dlEndpoint]);

	    /** Memoized WebChat styles */
	    const styleOptions = useMemo(
	        () => {
	            const common = {
	                hideUploadButton: true,
	                botAvatarInitials: "HB",
	                userAvatarInitials: "ME",
	                bubbleBorderRadius: 8,
	                bubbleFromUserBorderRadius: 8,
	                rootHeight: "100%",
	                bubbleMinHeight: 40,
	                bubbleMaxWidth: 480,
	            };

	            if (isDark) {
	                // Dark mode : bulles et input foncés
	                return {
	                    ...common,
	                    backgroundColor: "transparent",
	                    bubbleBackground: "#1f2937",          // messages du bot
	                    bubbleFromUserBackground: "#1f2937",   // messages de l'utilisateur
	                    bubbleTextColor: "#e5e7eb",
	                    bubbleFromUserTextColor: "#e5e7eb",
	                    sendBoxBackground: "#020617",
	                    sendBoxTextColor: "#e5e7eb",
	                    sendBoxBorderTop: "1px solid #1f2937",
	                    sendBoxPlaceholderColor: "#6b7280",
	                };
	            }

	            // Light mode : couleurs actuelles plus claires
	            return {
	                ...common,
	                bubbleBackground: "#E8F4FD",
	                bubbleFromUserBackground: "#5DADE2",
	                bubbleTextColor: "#1F2933",
	                bubbleFromUserTextColor: "#ffffff",
	                sendBoxBackground: "#ffffff",
	                sendBoxTextColor: "#1F2933",
	                sendBoxBorderTop: "1px solid #e5e7eb",
	                sendBoxPlaceholderColor: "#9ca3af",
	            };
	        },
	        [isDark]
	    );

    /** InitConversation (classic Health Bot) */
    const sendInitConversation = useCallback(
        (dl, opts = {}) => {
            const { triggeredScenario = null } = opts;
            const payload = {
                type: "invoke",
                name: "InitConversation",
                locale: "en-US",
                value: triggeredScenario ? { triggeredScenario } : {},
                from: { id: userId },
            };
            dl.postActivity(payload).subscribe(
                (id) => {
                    conversationReadyRef.current = true;
                },
                (err) => console.error("[HealthBot] InitConversation error:", err)
            );
        },
        [userId]
    );

    const sendText = useCallback(
        (dl, text) => {
            dl.postActivity({ type: "message", text, from: { id: userId } }).subscribe(
                (id) => undefined,
                (err) => console.error("[HealthBot] Failed to send message:", err)
            );
        },
        [userId]
    );

    /** Create DirectLine and wire listeners (also handles restart on endOfConversation) */
    const createDL = useCallback(
        (tkn) => {
            if (tkn && tkn === dlTokenRef.current && directLine) return;

            dlTokenRef.current = tkn;
            initSentRef.current = false;
            conversationReadyRef.current = false;

            statusSubRef.current?.unsubscribe?.();
            activitySubRef.current?.unsubscribe?.();

            const dl = createDirectLine({ token: tkn, domain: directLineDomain, webSocket: true });

            // Listen for activities to catch endOfConversation and soft-restart
            activitySubRef.current = dl.activity$.subscribe((activity) => {
                if (activity.type === "endOfConversation") {
                    console.warn("[HealthBot] endOfConversation received from bot");
                    if (!restartingRef.current) {
                        restartingRef.current = true;
                        setIsReady(false);
                        initSentRef.current = false;
                        conversationReadyRef.current = false;

                        // reuse current token, or mint a new one if missing
                        const doRestart = async () => {
                            let t = dlTokenRef.current;
                            if (!t) {
                                const again = await dispatch(hbGetToken({ userId }));
                                t = again?.payload?.token || null;
                            }
                            if (t) {
                                // small delay lets WebChat tear down the old stream
                                setTimeout(() => {
                                    createDL(t);
                                    restartingRef.current = false;
                                }, 150);
                            } else {
                                console.error("[HealthBot] No token available for restart");
                                restartingRef.current = false;
                            }
                        };
                        doRestart();
                    }
                }
            });

            statusSubRef.current = dl.connectionStatus$.subscribe(async (status) => {

                if (status === 2) {
                    setIsReady(true);
                    if (!initSentRef.current) {
                        initSentRef.current = true;
                        sendInitConversation(dl); // welcome menu
                    }
                }

                if (status === 3 || status === 4) {
                    console.warn("[HealthBot] Token expired/failed; requesting refresh…");
                    const action = await dispatch(hbRefreshToken());
                    const newToken = action?.payload?.token;

                    if (newToken && newToken !== dlTokenRef.current) {
                        setIsReady(false);
                        createDL(newToken);
                    } else if (!newToken) {
                        const again = await dispatch(hbGetToken({ userId }));
                        if (again?.payload?.token) {
                            setIsReady(false);
                            createDL(again.payload.token);
                        }
                    }
                }

                if (status === 5) {
                    console.warn("[HealthBot] Conversation ended unexpectedly (status=5)");
                    if (!restartingRef.current) {
                        restartingRef.current = true;
                        const t = dlTokenRef.current;
                        setTimeout(() => {
                            if (t) createDL(t);
                            restartingRef.current = false;
                        }, 150);
                    }
                }
            });

            setDirectLine(dl);
        },
        [directLine, directLineDomain, dispatch, sendInitConversation, userId]
    );

    // Ensure token then create DL
    useEffect(() => {
        (async () => {
            const res = await dispatch(hbEnsureToken({ userId }));
            const t = res?.payload?.token || token;
            if (!t) {
                console.error("[HealthBot] No token available");
                return;
            }
            setIsReady(false);
            createDL(t);
        })();

        return () => {
            statusSubRef.current?.unsubscribe?.();
            activitySubRef.current?.unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userId]);

    // Recreate if token really changes
    useEffect(() => {
        if (firstTokenRef.current) {
            firstTokenRef.current = false;
            return;
        }
        if (!token || token === dlTokenRef.current) return;
        setIsReady(false);
        createDL(token);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, expiresAt]);

    /** UI actions */
    const actions = [
        {
            id: "guide",
            tone: "info",
            title: t("assistant.actions.guide.title"),
            description: t("assistant.actions.guide.description"),
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                    <path d="M7.29102 10.417V22.917" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M15.6257 6.12467L14.584 10.4163H20.6569C20.9803 10.4163 21.2993 10.4916 21.5886 10.6363C21.8779 10.7809 22.1295 10.9909 22.3236 11.2497C22.5176 11.5084 22.6488 11.8088 22.7066 12.127C22.7645 12.4452 22.7475 12.7725 22.6569 13.083L20.2298 21.4163C20.1036 21.8491 19.8404 22.2292 19.4798 22.4997C19.1192 22.7701 18.6806 22.9163 18.2298 22.9163H4.16732C3.61478 22.9163 3.08488 22.6968 2.69418 22.3061C2.30348 21.9154 2.08398 21.3855 2.08398 20.833V12.4997C2.08398 11.9471 2.30348 11.4172 2.69418 11.0265C3.08488 10.6358 3.61478 10.4163 4.16732 10.4163H7.04232C7.42991 10.4161 7.80975 10.3078 8.13915 10.1035C8.46854 9.89929 8.73442 9.60719 8.9069 9.26009L12.5007 2.08301C12.9919 2.08909 13.4754 2.2061 13.915 2.4253C14.3547 2.64449 14.7391 2.9602 15.0396 3.34884C15.3401 3.73749 15.5489 4.18901 15.6504 4.66968C15.7519 5.15034 15.7434 5.64773 15.6257 6.12467Z" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            ),
            prompt: t("assistant.actions.guide.prompt"),
        },
        {
            id: "q",
            tone: "info",
            title: t("assistant.actions.q.title"),
            description: t("assistant.actions.q.description"),
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M8.228 9C8.402 7.837 9.352 7 10.5 7H13.5C14.88 7 16 8.12 16 9.5C16 10.88 14.88 12 13.5 12H10.5M10.5 12H13.5C14.88 12 16 13.12 16 14.5C16 15.88 14.88 17 13.5 17H10.5C9.352 17 8.402 16.163 8.228 15M10.5 12V7M10.5 12V17M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            ),
            prompt: t("assistant.actions.q.prompt"),
        },
        {
            id: "symptoms",
            tone: "info",
            title: t("assistant.actions.symptoms.title"),
            description: t("assistant.actions.symptoms.description"),
            icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 3V4M15 3V4M9 20V21M15 20V21M20 9H21M20 15H21M3 9H4M3 15H4M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            ),
            prompt: t("assistant.actions.symptoms.prompt"),
        },
    ];

    const inputRef = useRef(null);

    const handleAction = (a) => {
        if (!isReady || !directLine) {
            alert(t("assistant.connectingAlert"));
            return;
        }

        const scenario = scenarioMap[a.id];
        setShowChat(true);
        setTimeout(() => {
            const go = () => {
                if (!conversationReadyRef.current) {
                    setTimeout(go, 300);
                    return;
                }
                if (scenario) {
                    sendInitConversation(directLine, { triggeredScenario: { trigger: scenario, args: {} } });
                } else if (a.prompt) {
                    sendText(directLine, a.prompt);
                }
            };
            go();
        }, 500);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const text = message.trim();
        if (!text || !isReady || !directLine) return;

        setShowChat(true);
        setMessage("");

        setTimeout(() => {
            const go = () => {
                if (!conversationReadyRef.current) {
                    setTimeout(go, 300);
                    return;
                }
                sendText(directLine, text);
            };
            go();
        }, 500);

        inputRef.current?.focus();
    };

    return (
        <AppLayout>
            <div className="assistant-grid">
                <div className="assistant-page">
                    <header className="assistant-header">
                        <div
                            className="assistant-brand"
                            onClick={() => {
                                setShowChat(false);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        >
                            <span className="assistant-logo" aria-hidden>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
  <path d="M11.0174 2.81444C11.0603 2.58504 11.182 2.37786 11.3615 2.22876C11.5411 2.07966 11.7671 1.99805 12.0004 1.99805C12.2338 1.99805 12.4598 2.07966 12.6393 2.22876C12.8189 2.37786 12.9406 2.58504 12.9834 2.81444L14.0344 8.37244C14.1091 8.76759 14.3011 9.13106 14.5855 9.41541C14.8698 9.69977 15.2333 9.8918 15.6284 9.96644L21.1864 11.0174C21.4158 11.0603 21.623 11.182 21.7721 11.3615C21.9212 11.5411 22.0028 11.7671 22.0028 12.0004C22.0028 12.2338 21.9212 12.4598 21.7721 12.6393C21.623 12.8189 21.4158 12.9406 21.1864 12.9834L15.6284 14.0344C15.2333 14.1091 14.8698 14.3011 14.5855 14.5855C14.3011 14.8698 14.1091 15.2333 14.0344 15.6284L12.9834 21.1864C12.9406 21.4158 12.8189 21.623 12.6393 21.7721C12.4598 21.9212 12.2338 22.0028 12.0004 22.0028C11.7671 22.0028 11.5411 21.9212 11.3615 21.7721C11.182 21.623 11.0603 21.4158 11.0174 21.1864L9.96644 15.6284C9.8918 15.2333 9.69977 14.8698 9.41541 14.5855C9.13106 14.3011 8.76759 14.1091 8.37244 14.0344L2.81444 12.9834C2.58504 12.9406 2.37786 12.8189 2.22876 12.6393C2.07966 12.4598 1.99805 12.2338 1.99805 12.0004C1.99805 11.7671 2.07966 11.5411 2.22876 11.3615C2.37786 11.182 2.58504 11.0603 2.81444 11.0174L8.37244 9.96644C8.76759 9.8918 9.13106 9.69977 9.41541 9.41541C9.69977 9.13106 9.8918 8.76759 9.96644 8.37244L11.0174 2.81444Z" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M20 2V6" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M22 4H18" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M4 22C5.10457 22 6 21.1046 6 20C6 18.8954 5.10457 18 4 18C2.89543 18 2 18.8954 2 20C2 21.1046 2.89543 22 4 22Z" stroke="#1e1e1e" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>                                
                            </span>
	                            <span className="assistant-brand-text">{t("assistant.brand")}</span>
                        </div>

                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
	                            {showChat && (
	                                <button className="assistant-history-btn" onClick={() => setShowChat(false)} type="button">
	                                    ← {t("assistant.back")}
	                                </button>
	                            )}
                        </div>
                    </header>

                    <main className="assistant-main" role="main">
                        {!showChat && (
                            <>
                             <div className="assistant-hero">
                             <div className="assistant-bg-robots" aria-hidden="true">
  <img className="assistant-bg-robot left" src={RobotLeft} alt="" draggable="false" />
  <img className="assistant-bg-robot right" src={RobotRight} alt="" draggable="false" />
</div>

                  <img
                    src={RobotIllustration}
                    alt="Medical Assistant"
                    className="assistant-hero-robot"
                    draggable="false"
                  />
                </div>
                                {/* <div className="assistant-orb" aria-hidden>
                                    <div className="assistant-orb-core" />
                                </div> */}

                                <h1 className="assistant-title">
                                    {t("assistant.greeting", { name: userName })}
                                    <br />
                                    <span className="assistant-title-ask">{t("assistant.greetingAsk")}</span>
                                </h1>

                                <p className="assistant-subtitle">
                                    {isReady ? t("assistant.subtitleReady") : t("assistant.subtitleConnecting")}
                                </p>

                                <div className="assistant-actions-grid" role="list">
                                    {actions.map((a) => (
                                        <button
                                            key={a.id}
                                            role="listitem"
                                            className={`assistant-action-card tone-${a.tone}`}
                                            onClick={() => handleAction(a)}
                                            disabled={!isReady}
                                            style={{ opacity: isReady ? 1 : 0.6, cursor: isReady ? "pointer" : "not-allowed" }}
                                        >
                                            <span className="action-icon" aria-hidden>
                                                {a.icon}
                                            </span>
                                            <span className="action-content">
                                                <span className="action-title">{a.title}</span>
                                                <span className="action-description">{a.description}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        {showChat && (
                            <div className="assistant-chat-wrapper">
	                                {directLine && isReady ? (
                                    <ReactWebChat
                                        key={dlTokenRef.current || "dl"}     // clean remount on token change only
                                        className="webchat-component"
                                        directLine={directLine}
                                        userID={userId}
                                        locale="en-US"
                                        styleOptions={styleOptions}
                                    />
	                                ) : (
	                                    <div className="assistant-chat-loading">
	                                        <div className="loading-spinner"></div>
	                                        <p>{t("assistant.connectingChat")}</p>
	                                    </div>
	                                )}
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </AppLayout>
    );
}
